import axios from 'axios';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { WordCloud as CustomWordCloud, SearchBar, Header, SuggestionList, Footer, SummaryCard, WordCard, Dropdown, ComparisonView, KeywordNetwork, SearchVolumeCard, RelatedKeywordsCard, MyPage, AdminPage, IntentAnalysisCard, AiInsightsCard, AiPersonasCard, LandingPage, LegalPage} from './components';
import type { KeywordNetworkData } from './components/KeywordNetwork';
import type { SearchVolumeData } from './components/SearchVolumeCard';
import type { RelatedKeywordsData } from './components/RelatedKeywordsCard';
import type { AiInsightsData } from './components/AiInsightsCard';
import type { AiPersona, AiPersonasData } from './components/AiPersonasCard';
import type { SearchHistoryItem } from './components/RecentSearches';
import type { SavedKeywordItem } from './components/SavedKeywords';
import type { UsageSummary } from './components/UsagePlanCard';
import type { ContactMessageItem } from './components/AdminPage';
import { useAuth } from './auth/AuthContext';

type SuggestService = "google" | "amazon" | "youtube" | "rakuten" | "tiktok" | "bing" | "appstore" | "googleplay" | "chiebukuro";

type SuggestionGroup = {
  suffix: string;
  suggestions: string[];
};

type SuggestResponse = {
  groupedSuggestions: SuggestionGroup[];
  topSuggestions: string[];
  wordCloudWords?: WordCloudWord[];
};

type ComparisonResult = {
  common: string[];
  leftOnly: string[];
  rightOnly: string[];
  leftCloudWords: WordCloudWord[];
  rightCloudWords: WordCloudWord[];
};

type WordCloudWord = {
  text: string;
  value: number;
};

type SearchMode = "single" | "compare";
type AppPage = "analysis" | "mypage" | "admin";
type PublicPage = "terms" | "privacy" | "contact";
type UpgradeRequestItem = {
  id: string;
  uid: string;
  email?: string | null;
  name?: string | null;
  currentPlan?: string;
  requestedPlan: 'basic' | 'premium' | string;
  message?: string;
  status: 'new' | 'approved' | 'rejected' | string;
  createdAtMs?: number;
};
type UsageLimitNotice = {
  feature?: string;
  featureLabel?: string;
  plan?: string;
  limit?: number;
  used?: number;
  scope?: string;
  message: string;
};

const TOP_PROMPTS = [
  "どのニーズを調べますか？",
  "今日はどんな検索意図を見にいきますか？",
  "どのキーワードの需要を探りますか？",
  "ユーザーの悩みをどこから掘りますか？",
  "次に伸びそうなテーマを調べましょう",
  "どんな言葉の裏側を見てみますか？",
];

const getPublicPageFromPath = (): PublicPage | null => {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/terms') return 'terms';
  if (path === '/privacy') return 'privacy';
  if (path === '/contact') return 'contact';
  return null;
};


const App = () => {
  const {
    user,
    loading: authLoading,
    enabled: authEnabled,
    error: authError,
    loginWithGoogle,
    sendEmailLink,
    getIdToken,
    logout,
  } = useAuth();
  const [input, setInput] = useState('');
  const [page, setPage] = useState<AppPage>("analysis");
  
  const [words, setWords] = useState<WordCloudWord[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupedSuggestions, setGroupedSuggestions] = useState<SuggestionGroup[]>([]);
  const [topSuggestions, setTopSuggestions] = useState<string[]>([]); // 🔸追加
  const [mode, setMode] = useState<SearchMode>("single");
  const [compareService, setCompareService] = useState<SuggestService>("bing");
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [volumeData, setVolumeData] = useState<SearchVolumeData | null>(null);
  const [volumeCached, setVolumeCached] = useState(false);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const [relatedKeywordsData, setRelatedKeywordsData] = useState<RelatedKeywordsData | null>(null);
  const [relatedKeywordsCached, setRelatedKeywordsCached] = useState(false);
  const [relatedKeywordsLoading, setRelatedKeywordsLoading] = useState(false);
  const [relatedKeywordsError, setRelatedKeywordsError] = useState<string | null>(null);
  const [aiInsightsData, setAiInsightsData] = useState<AiInsightsData | null>(null);
  const [aiInsightsCached, setAiInsightsCached] = useState(false);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState<string | null>(null);
  const [aiPersonasData, setAiPersonasData] = useState<AiPersonasData | null>(null);
  const [aiPersonasCached, setAiPersonasCached] = useState(false);
  const [aiPersonasLoading, setAiPersonasLoading] = useState(false);
  const [aiPersonasError, setAiPersonasError] = useState<string | null>(null);
  const [personaImages, setPersonaImages] = useState<Record<string, string>>({});
  const [personaImageLoading, setPersonaImageLoading] = useState<Record<string, boolean>>({});
  const [personaImageErrors, setPersonaImageErrors] = useState<Record<string, string>>({});
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(false);
  const [savedKeywords, setSavedKeywords] = useState<SavedKeywordItem[]>([]);
  const [savedKeywordsLoading, setSavedKeywordsLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRequests, setAdminRequests] = useState<UpgradeRequestItem[]>([]);
  const [adminRequestsLoading, setAdminRequestsLoading] = useState(false);
  const [adminContactMessages, setAdminContactMessages] = useState<ContactMessageItem[]>([]);
  const [adminContactMessagesLoading, setAdminContactMessagesLoading] = useState(false);
  const [adminActionLoadingId, setAdminActionLoadingId] = useState<string | null>(null);
  const publicPage = getPublicPageFromPath();
  const [searchError, setSearchError] = useState<string | null>(null);
  const [usageLimitNotice, setUsageLimitNotice] = useState<UsageLimitNotice | null>(null);

  const [showResult, setShowResult] = useState(false);
  const topPrompt = useMemo(
    () => TOP_PROMPTS[Math.floor(Math.random() * TOP_PROMPTS.length)],
    []
  );

  // const [service, setService] = useState<"google" | "amazon">("google");

  const [service, setService] = useState<SuggestService>("google");

//   const options = [
//   { label: "Google", value: "google" },
//   { label: "Amazon", value: "amazon" },
// ] as const;

const options = [
  { label: "Google", value: "google" },
  { label: "Amazon", value: "amazon" },
  { label: "YouTube", value: "youtube" },
  { label: "楽天", value: "rakuten" },
  { label: "TikTok", value: "tiktok" },
  { label: "Bing", value: "bing" },
  { label: "App Store", value: "appstore" },
  { label: "Google Play β", value: "googleplay" },
  { label: "Yahoo知恵袋 β", value: "chiebukuro" },
  // { label: "eBay", value: "ebay" },
] as const;

  const getServiceLabel = (value: SuggestService) =>
    options.find((option) => option.value === value)?.label || value;

  const API_ORIGIN =
    process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as any;
      if (data?.code === 'USAGE_LIMIT_EXCEEDED') {
        setUsageLimitNotice({
          feature: data.feature,
          featureLabel: data.featureLabel,
          plan: data.plan,
          limit: data.limit,
          used: data.used,
          scope: data.scope,
          message: data.message || '利用上限に達しました。',
        });
        void fetchUsageSummary();
      }
      return data?.message || data?.error || fallback;
    }

    return fallback;
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(async (config) => {
      const requestUrl = config.url || '';
      const shouldAttachToken = API_ORIGIN
        ? requestUrl.startsWith(API_ORIGIN)
        : requestUrl.startsWith('/api/');

      if (!shouldAttachToken) return config;

      const token = await getIdToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        } as any;
      }

      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [API_ORIGIN, getIdToken]);

  const fetchSearchHistory = useCallback(async () => {
    if (!user) {
      setSearchHistory([]);
      setSearchHistoryLoading(false);
      return;
    }

    setSearchHistoryLoading(true);
    try {
      const res = await axios.get(`${API_ORIGIN}/api/search-history`, {
        params: { limit: 12 },
      });
      setSearchHistory(res.data.searches || []);
    } catch (error) {
      console.warn('検索履歴の取得に失敗:', error);
    } finally {
      setSearchHistoryLoading(false);
    }
  }, [API_ORIGIN, user]);

  useEffect(() => {
    void fetchSearchHistory();
  }, [fetchSearchHistory]);

  const fetchSavedKeywords = useCallback(async () => {
    if (!user) {
      setSavedKeywords([]);
      setSavedKeywordsLoading(false);
      return;
    }

    setSavedKeywordsLoading(true);
    try {
      const res = await axios.get(`${API_ORIGIN}/api/saved-keywords`, {
        params: { limit: 100 },
      });
      setSavedKeywords(res.data.keywords || []);
    } catch (error) {
      console.warn('保存キーワードの取得に失敗:', error);
    } finally {
      setSavedKeywordsLoading(false);
    }
  }, [API_ORIGIN, user]);

  useEffect(() => {
    void fetchSavedKeywords();
  }, [fetchSavedKeywords]);

  const fetchUsageSummary = useCallback(async () => {
    if (!user) {
      setUsageSummary(null);
      setUsageLoading(false);
      return;
    }

    setUsageLoading(true);
    try {
      const res = await axios.get(`${API_ORIGIN}/api/usage`);
      setUsageSummary(res.data.usage || null);
    } catch (error) {
      console.warn('利用状況の取得に失敗:', error);
    } finally {
      setUsageLoading(false);
    }
  }, [API_ORIGIN, user]);

  useEffect(() => {
    void fetchUsageSummary();
  }, [fetchUsageSummary]);

  const fetchAdminStatus = useCallback(async () => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      await axios.get(`${API_ORIGIN}/api/admin/me`);
      setIsAdmin(true);
    } catch {
      setIsAdmin(false);
    }
  }, [API_ORIGIN, user]);

  useEffect(() => {
    void fetchAdminStatus();
  }, [fetchAdminStatus]);

  const fetchAdminRequests = useCallback(async () => {
    if (!user || !isAdmin) {
      setAdminRequests([]);
      setAdminRequestsLoading(false);
      return;
    }

    setAdminRequestsLoading(true);
    try {
      const res = await axios.get(`${API_ORIGIN}/api/admin/upgrade-requests`, {
        params: { status: 'all', limit: 50 },
      });
      setAdminRequests(res.data.requests || []);
    } catch (error) {
      console.warn('管理画面の申請取得に失敗:', error);
    } finally {
      setAdminRequestsLoading(false);
    }
  }, [API_ORIGIN, isAdmin, user]);

  const fetchAdminContactMessages = useCallback(async () => {
    if (!user || !isAdmin) {
      setAdminContactMessages([]);
      setAdminContactMessagesLoading(false);
      return;
    }

    setAdminContactMessagesLoading(true);
    try {
      const res = await axios.get(`${API_ORIGIN}/api/admin/contact-messages`, {
        params: { status: 'all', limit: 50 },
      });
      setAdminContactMessages(res.data.messages || []);
    } catch (error) {
      console.warn('管理画面のお問い合わせ取得に失敗:', error);
    } finally {
      setAdminContactMessagesLoading(false);
    }
  }, [API_ORIGIN, isAdmin, user]);

  useEffect(() => {
    if (page === 'admin') {
      void fetchAdminRequests();
      void fetchAdminContactMessages();
    }
  }, [fetchAdminContactMessages, fetchAdminRequests, page]);

  useEffect(() => {
    if (!user && page !== "analysis") {
      setPage("analysis");
    }
    if (page === 'admin' && !isAdmin) {
      setPage("analysis");
    }
  }, [isAdmin, page, user]);

  const saveSearchHistory = async (payload: {
    keyword: string;
    service: SuggestService;
    mode: SearchMode;
    compareService?: SuggestService;
    suggestionCount?: number;
    commonCount?: number;
    leftOnlyCount?: number;
    rightOnlyCount?: number;
    wordCount?: number;
    topSuggestions?: string[];
  }) => {
    if (!user) return;

    try {
      const res = await axios.post(`${API_ORIGIN}/api/search-history`, payload);
      const savedItem = res.data?.id
        ? {
            id: res.data.id,
            ...payload,
            createdAtMs: Date.now(),
          }
        : null;

      if (savedItem) {
        setSearchHistory((current) => [
          savedItem,
          ...current.filter((item) => item.id !== savedItem.id),
        ].slice(0, 12));
      }
    } catch (error) {
      console.warn('検索履歴の保存に失敗:', error);
    }
  };

  const getSavedKeywordKey = useCallback(
    (keyword: string, targetService: SuggestService = service) =>
      `${targetService}:${keyword.trim().toLowerCase()}`,
    [service]
  );

  const savedKeywordKeys = useMemo(
    () => new Set(savedKeywords.map((item) => getSavedKeywordKey(item.keyword, item.service as SuggestService))),
    [getSavedKeywordKey, savedKeywords]
  );

  const isKeywordSaved = (keyword: string, targetService: SuggestService = service) =>
    savedKeywordKeys.has(getSavedKeywordKey(keyword, targetService));

  const saveKeyword = async (payload: {
    keyword: string;
    source?: string;
    searchVolume?: number | null;
    cpc?: number | null;
    competition?: string | null;
  }) => {
    const keyword = payload.keyword.trim();
    if (!keyword || !user || isKeywordSaved(keyword)) return;

    const optimisticId = getSavedKeywordKey(keyword).replace(/[^a-z0-9:_-]/gi, '_');
    const optimisticItem: SavedKeywordItem = {
      id: optimisticId,
      keyword,
      service,
      source: payload.source || 'suggestion',
      searchVolume: payload.searchVolume ?? null,
      cpc: payload.cpc ?? null,
      competition: payload.competition ?? null,
      createdAtMs: Date.now(),
    };

    setSavedKeywords((current) => [optimisticItem, ...current]);

    try {
      const res = await axios.post(`${API_ORIGIN}/api/saved-keywords`, {
        ...payload,
        keyword,
        service,
      });
      const savedItem = res.data?.id
        ? {
            id: res.data.id,
            ...res.data.data,
            createdAtMs: res.data.data?.createdAtMs || Date.now(),
          }
        : optimisticItem;

      setSavedKeywords((current) => [
        savedItem,
        ...current.filter((item) => getSavedKeywordKey(item.keyword, item.service as SuggestService) !== getSavedKeywordKey(keyword)),
      ]);
    } catch (error) {
      console.warn('キーワード保存に失敗:', error);
      setSavedKeywords((current) => current.filter((item) => item.id !== optimisticId));
    }
  };

  const fetchSuggestions = async (
    keyword: string,
    targetService: SuggestService
  ): Promise<SuggestResponse> => {
    const res = await axios.get(`${API_ORIGIN}/api/suggest`, {
      params: {
        q: keyword,
        service: targetService,
      },
    });

    return res.data;
  };

  const fetchSearchVolume = async (
    keyword: string,
    refresh = false,
    force = false
  ): Promise<{ data: SearchVolumeData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.get(`${API_ORIGIN}/api/search-volume`, {
        params: {
          q: keyword,
          refresh: refresh ? 1 : 0,
          force: force ? 1 : 0,
        },
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('検索ボリューム取得失敗:', error);
      const message = getApiErrorMessage(error, '検索ボリュームを取得できませんでした。');

      return {
        data: null,
        cached: false,
        error: message,
      };
    }
  };

  const fetchRelatedKeywords = async (
    keyword: string,
    refresh = false,
    force = false
  ): Promise<{ data: RelatedKeywordsData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.get(`${API_ORIGIN}/api/related-keywords`, {
        params: {
          q: keyword,
          refresh: refresh ? 1 : 0,
          force: force ? 1 : 0,
        },
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('関連キーワード取得失敗:', error);
      const message = getApiErrorMessage(error, '関連キーワードを取得できませんでした。');

      return {
        data: null,
        cached: false,
        error: message,
      };
    }
  };

  const fetchCachedAiInsights = async (
    keyword: string,
    targetService: SuggestService
  ): Promise<{ data: AiInsightsData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.get(`${API_ORIGIN}/api/ai-insights`, {
        params: {
          q: keyword,
          service: targetService,
        },
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('AIインサイトキャッシュ取得失敗:', error);
      return {
        data: null,
        cached: false,
      };
    }
  };

  const generateAiInsights = async (
    keyword: string,
    targetService: SuggestService,
    force = false
  ): Promise<{ data: AiInsightsData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.post(`${API_ORIGIN}/api/ai-insights`, {
        keyword,
        service: targetService,
        refresh: true,
        force,
        suggestions: flattenSuggestions(groupedSuggestions, topSuggestions),
        topSuggestions,
        wordCloudWords: words,
        searchVolume: volumeData,
        relatedKeywords: relatedKeywordsData?.keywords || [],
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('AIインサイト生成失敗:', error);
      const message = getApiErrorMessage(error, 'AIインサイトを生成できませんでした。');

      return {
        data: null,
        cached: false,
        error: message,
      };
    }
  };

  const fetchCachedAiPersonas = async (
    keyword: string,
    targetService: SuggestService
  ): Promise<{ data: AiPersonasData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.get(`${API_ORIGIN}/api/ai-personas`, {
        params: {
          q: keyword,
          service: targetService,
        },
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('ペルソナ仮説キャッシュ取得失敗:', error);
      return {
        data: null,
        cached: false,
      };
    }
  };

  const generateAiPersonas = async (
    keyword: string,
    targetService: SuggestService,
    force = false
  ): Promise<{ data: AiPersonasData | null; cached: boolean; error?: string }> => {
    try {
      const res = await axios.post(`${API_ORIGIN}/api/ai-personas`, {
        keyword,
        service: targetService,
        refresh: true,
        force,
        suggestions: flattenSuggestions(groupedSuggestions, topSuggestions),
        topSuggestions,
        wordCloudWords: words,
        searchVolume: volumeData,
        relatedKeywords: relatedKeywordsData?.keywords || [],
      });

      return {
        data: res.data.data || null,
        cached: Boolean(res.data.cached),
      };
    } catch (error) {
      console.error('ペルソナ仮説生成失敗:', error);
      const message = getApiErrorMessage(error, 'ペルソナ仮説を生成できませんでした。');

      return {
        data: null,
        cached: false,
        error: message,
      };
    }
  };

  const getPersonaImageKey = (persona: AiPersona, index: number) =>
    `${index}:${persona.name}:${persona.segment}`;

  const generatePersonaImage = async (
    keyword: string,
    targetService: SuggestService,
    persona: AiPersona,
    index: number,
    force = false
  ): Promise<{ image: string | null; error?: string }> => {
    try {
      const res = await axios.post(`${API_ORIGIN}/api/persona-image`, {
        keyword,
        service: targetService,
        persona,
        refresh: true,
        force,
      });
      const image = res.data.data?.image || null;

      return {
        image,
        error: image ? undefined : 'ペルソナ画像を生成できませんでした。',
      };
    } catch (error) {
      console.error('ペルソナ画像生成失敗:', error);
      const message = getApiErrorMessage(error, 'ペルソナ画像を生成できませんでした。');

      return {
        image: null,
        error: message,
      };
    }
  };

  const flattenSuggestions = (groups: SuggestionGroup[], top: string[]) => {
    const all = [
      ...top,
      ...groups.flatMap((group) => group.suggestions),
    ];

    return all
      .map((word) => word.trim())
      .filter((word, index, list) => word.length > 0 && list.indexOf(word) === index);
  };

  const compareSuggestions = (
    keyword: string,
    left: SuggestResponse,
    right: SuggestResponse
  ): ComparisonResult => {
    const leftWords = flattenSuggestions(left.groupedSuggestions, left.topSuggestions);
    const rightWords = flattenSuggestions(right.groupedSuggestions, right.topSuggestions);
    const leftMap = new Map(leftWords.map((word) => [word.toLowerCase(), word]));
    const rightMap = new Map(rightWords.map((word) => [word.toLowerCase(), word]));

    const common = leftWords.filter((word) => rightMap.has(word.toLowerCase()));
    const leftOnly = leftWords.filter((word) => !rightMap.has(word.toLowerCase()));
    const rightOnly = rightWords.filter((word) => !leftMap.has(word.toLowerCase()));

    return {
      common,
      leftOnly,
      rightOnly,
      leftCloudWords: buildWordCloudDataFromSuggestions(keyword, leftOnly, 80, 1),
      rightCloudWords: buildWordCloudDataFromSuggestions(keyword, rightOnly, 80, 1),
    };
  };

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const buildWordCloudDataFromSuggestions = (
    keyword: string,
    suggestions: string[],
    limit = 200,
    minCount = 2
  ): WordCloudWord[] => {
    const counts: { [key: string]: number } = {};
    const normalizedKeyword = keyword.trim();
    const keywordPattern = normalizedKeyword
      ? new RegExp(escapeRegExp(normalizedKeyword), 'gi')
      : null;

    for (const word of suggestions) {
      const rest = keywordPattern ? word.replace(keywordPattern, '').trim() : word.trim();
      if (rest) {
        const parts = rest.split(/\s+/);
        for (const part of parts) {
          if (part && part.toLowerCase() !== normalizedKeyword.toLowerCase()) {
            counts[part] = (counts[part] || 0) + 1;
          }
        }
      }
    }

    const sortedCounts = Object.entries(counts)
      .filter(([_, count]) => count >= minCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit);

    return sortedCounts.map(([text, value]) => ({ text, value }));
  };

  const buildWordCloudData = (keyword: string, groups: SuggestionGroup[]) => {
    const allSuggestions = groups
      .flatMap((group) => group.suggestions)
      .filter((word) => word.trim() !== keyword.trim());

    return buildWordCloudDataFromSuggestions(keyword, allSuggestions);
  };

  const buildKeywordNetworkData = (
    keyword: string,
    cloudWords: WordCloudWord[],
    groups: SuggestionGroup[],
    top: string[]
  ): KeywordNetworkData => {
    const normalizedKeyword = keyword.trim();
    if (!normalizedKeyword || cloudWords.length === 0) {
      return { nodes: [], links: [] };
    }

    const nodeValues = new Map<string, number>();
    const linkValues = new Map<string, KeywordNetworkData["links"][number]>();
    const sourcePhrases = [
      ...top,
      ...groups.flatMap((group) => group.suggestions),
    ];
    const networkWords = cloudWords.slice(0, 36);
    const maxWordValue = Math.max(...networkWords.map((word) => word.value), 1);

    nodeValues.set(normalizedKeyword, maxWordValue + 4);

    const addNode = (id: string, value: number) => {
      const trimmed = id.trim();
      if (!trimmed) return;
      nodeValues.set(trimmed, Math.max(nodeValues.get(trimmed) || 0, value));
    };

    const addLink = (source: string, target: string, value: number) => {
      if (!source || !target || source === target) return;
      const [left, right] = [source, target].sort((a, b) => a.localeCompare(b, 'ja'));
      const key = `${left}__${right}`;
      const current = linkValues.get(key);

      linkValues.set(key, {
        source: left,
        target: right,
        value: (current?.value || 0) + value,
      });
    };

    for (const word of networkWords) {
      addNode(word.text, word.value);
      addLink(normalizedKeyword, word.text, Math.max(1, word.value));
    }

    for (const phrase of sourcePhrases) {
      const includedWords = networkWords
        .filter((word) => phrase.includes(word.text))
        .slice(0, 6);

      for (let i = 0; i < includedWords.length; i += 1) {
        for (let j = i + 1; j < includedWords.length; j += 1) {
          addLink(includedWords[i].text, includedWords[j].text, 1);
        }
      }
    }

    return {
      nodes: Array.from(nodeValues.entries()).map(([id, value]) => ({ id, value })),
      links: Array.from(linkValues.values())
        .sort((a, b) => b.value - a.value)
        .slice(0, 120),
    };
  };

  const keywordNetworkData = useMemo(
    () => buildKeywordNetworkData(input, words, groupedSuggestions, topSuggestions),
    [input, words, groupedSuggestions, topSuggestions]
  );

  const intentAnalysisSuggestions = useMemo(
    () => flattenSuggestions(groupedSuggestions, topSuggestions),
    [groupedSuggestions, topSuggestions]
  );



  // 入力が空になったら自動で非表示
  useEffect(() => {
    if (input.trim() === '') {
      setShowResult(false);
      setComparisonResult(null);
      setVolumeData(null);
      setVolumeCached(false);
      setVolumeLoading(false);
      setVolumeError(null);
      setAiInsightsData(null);
      setAiInsightsCached(false);
      setAiInsightsLoading(false);
      setAiInsightsError(null);
      setAiPersonasData(null);
      setAiPersonasCached(false);
      setAiPersonasLoading(false);
      setAiPersonasError(null);
      setPersonaImages({});
      setPersonaImageLoading({});
      setPersonaImageErrors({});
    }
  }, [input]); // inputが変わるたびにこの処理が走る

  const WORD_CLOUD_EXPORT_WIDTH = 1400;

  const getSvgExportSize = (svg: SVGSVGElement) => {
    const viewBox = svg.viewBox.baseVal;
    const rect = svg.getBoundingClientRect();
    const sourceWidth = viewBox.width || rect.width || 700;
    const sourceHeight = viewBox.height || rect.height || 400;
    const scale = WORD_CLOUD_EXPORT_WIDTH / sourceWidth;

    return {
      width: WORD_CLOUD_EXPORT_WIDTH,
      height: Math.round(sourceHeight * scale),
    };
  };

  const downloadSVGAsPNG = () => {
    const svg = document.querySelector('svg') as SVGSVGElement | null;
    if (!svg) {
      console.warn('SVG not found');
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const exportSize = getSvgExportSize(svg);
      const canvas = document.createElement('canvas');
      canvas.width = exportSize.width;
      canvas.height = exportSize.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // ✅ 背景を白く塗る
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // WordCloudを描画
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'wordcloud.png';
      link.href = pngUrl;
      link.click();
    };

    img.src = url;
  };

  const copyImageToClipboard = (): Promise<boolean> => {
    const svg = document.querySelector('svg') as SVGSVGElement | null;
    if (!svg) {
      console.warn('SVG not found');
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = async () => {
        const exportSize = getSvgExportSize(svg);
        const canvas = document.createElement('canvas');
        canvas.width = exportSize.width;
        canvas.height = exportSize.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          resolve(false);
          return;
        }

        // 背景を白で塗る
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);

        // ✅ PNG blob に変換
        canvas.toBlob(async (blob) => {
          if (!blob) {
            resolve(false);
            return;
          }

          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
            resolve(true);
          } catch (err) {
            console.error('クリップボードコピーに失敗:', err);
            resolve(false);
          }
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  };


  const handleSearch = async (
    query?: string,
    modeOverride?: SearchMode,
    serviceOverride?: SuggestService,
    compareServiceOverride?: SuggestService
  ) => {

  const keyword = typeof query === 'string' ? query.trim() : input.trim();
  if (!keyword) return;

  const activeMode = modeOverride || mode;
  const activeService = serviceOverride || service;
  const activeCompareService = compareServiceOverride || compareService;

  setLoading(true);
  setShowResult(false);
  setSearchError(null);
  setUsageLimitNotice(null);
  setComparisonResult(null);
  setVolumeData(null);
  setVolumeCached(false);
  setVolumeError(null);
  setRelatedKeywordsData(null);
  setRelatedKeywordsCached(false);
  setRelatedKeywordsError(null);
  setAiInsightsData(null);
  setAiInsightsCached(false);
  setAiInsightsError(null);
  setAiPersonasData(null);
  setAiPersonasCached(false);
  setAiPersonasError(null);
  setPersonaImages({});
  setPersonaImageLoading({});
  setPersonaImageErrors({});

  try {
    if (activeMode === "compare") {
      const [left, right] = await Promise.all([
        fetchSuggestions(keyword, activeService),
        fetchSuggestions(keyword, activeCompareService),
      ]);

      setTopSuggestions([]);
      setGroupedSuggestions([]);
      setWords([]);
      const comparison = compareSuggestions(keyword, left, right);
      setComparisonResult(comparison);
      setShowResult(true);
      void fetchUsageSummary();
      void saveSearchHistory({
        keyword,
        service: activeService,
        mode: 'compare',
        compareService: activeCompareService,
        suggestionCount: comparison.common.length + comparison.leftOnly.length + comparison.rightOnly.length,
        commonCount: comparison.common.length,
        leftOnlyCount: comparison.leftOnly.length,
        rightOnlyCount: comparison.rightOnly.length,
        wordCount: comparison.leftCloudWords.length + comparison.rightCloudWords.length,
        topSuggestions: comparison.common.slice(0, 10),
      });
      return;
    }

    const [res, volume, relatedKeywords, aiInsights, aiPersonas] = await Promise.all([
      fetchSuggestions(keyword, activeService),
      fetchSearchVolume(keyword, false),
      fetchRelatedKeywords(keyword, false),
      fetchCachedAiInsights(keyword, activeService),
      fetchCachedAiPersonas(keyword, activeService),
    ]);

    setTopSuggestions(res.topSuggestions);
    setGroupedSuggestions(res.groupedSuggestions);
    setWords(res.wordCloudWords || buildWordCloudData(keyword, res.groupedSuggestions));
    setVolumeData(volume.data);
    setVolumeCached(volume.cached);
    setVolumeError(volume.error || null);
    setRelatedKeywordsData(relatedKeywords.data);
    setRelatedKeywordsCached(relatedKeywords.cached);
    setRelatedKeywordsError(relatedKeywords.error || null);
    setAiInsightsData(aiInsights.data);
    setAiInsightsCached(aiInsights.cached);
    setAiInsightsError(aiInsights.error || null);
    setAiPersonasData(aiPersonas.data);
    setAiPersonasCached(aiPersonas.cached);
    setAiPersonasError(aiPersonas.error || null);
    setShowResult(true);
    void fetchUsageSummary();
    void saveSearchHistory({
      keyword,
      service: activeService,
      mode: 'single',
      suggestionCount: flattenSuggestions(res.groupedSuggestions, res.topSuggestions).length,
      wordCount: (res.wordCloudWords || buildWordCloudData(keyword, res.groupedSuggestions)).length,
      topSuggestions: res.topSuggestions.slice(0, 10),
    });
  } catch (error) {
    console.error('サジェスト取得失敗:', error);
    setSearchError(getApiErrorMessage(error, 'サジェストを取得できませんでした。'));
    setShowResult(false);
  } finally {
    setLoading(false);
  }
};

const handleFetchVolume = async (force = false) => {
  const keyword = input.trim();
  if (!keyword || volumeLoading) return;

  setVolumeLoading(true);
  setVolumeError(null);
  setUsageLimitNotice(null);

  const result = await fetchSearchVolume(keyword, true, force);
  setVolumeData(result.data);
  setVolumeCached(result.cached);
  setVolumeError(result.error || null);
  void fetchUsageSummary();
  setVolumeLoading(false);
};

const handleFetchRelatedKeywords = async (force = false) => {
  const keyword = input.trim();
  if (!keyword || relatedKeywordsLoading) return;

  setRelatedKeywordsLoading(true);
  setRelatedKeywordsError(null);
  setUsageLimitNotice(null);

  const result = await fetchRelatedKeywords(keyword, true, force);
  setRelatedKeywordsData(result.data);
  setRelatedKeywordsCached(result.cached);
  setRelatedKeywordsError(result.error || null);
  void fetchUsageSummary();
  setRelatedKeywordsLoading(false);
};

const handleFetchAiInsights = async (force = false) => {
  const keyword = input.trim();
  if (!keyword || aiInsightsLoading) return;

  setAiInsightsLoading(true);
  setAiInsightsError(null);
  setUsageLimitNotice(null);

  const result = await generateAiInsights(keyword, service, force);
  setAiInsightsData(result.data);
  setAiInsightsCached(result.cached);
  setAiInsightsError(result.error || null);
  void fetchUsageSummary();
  setAiInsightsLoading(false);
};

const handleFetchAiPersonas = async (force = false) => {
  const keyword = input.trim();
  if (!keyword || aiPersonasLoading) return;

  setAiPersonasLoading(true);
  setAiPersonasError(null);
  setUsageLimitNotice(null);

  const result = await generateAiPersonas(keyword, service, force);
  setAiPersonasData(result.data);
  setAiPersonasCached(result.cached);
  setAiPersonasError(result.error || null);
  void fetchUsageSummary();
  setAiPersonasLoading(false);
};

const handleGeneratePersonaImage = async (persona: AiPersona, index: number, force = false) => {
  const keyword = input.trim();
  if (!keyword) return;

  const imageKey = getPersonaImageKey(persona, index);
  if (personaImageLoading[imageKey]) return;

  setPersonaImageLoading((current) => ({ ...current, [imageKey]: true }));
  setPersonaImageErrors((current) => {
    const next = { ...current };
    delete next[imageKey];
    return next;
  });
  setUsageLimitNotice(null);

  const result = await generatePersonaImage(keyword, service, persona, index, force);
  if (result.image) {
    setPersonaImages((current) => ({ ...current, [imageKey]: result.image as string }));
  }
  if (result.error) {
    setPersonaImageErrors((current) => ({ ...current, [imageKey]: result.error || 'ペルソナ画像を生成できませんでした。' }));
  }
  void fetchUsageSummary();
  setPersonaImageLoading((current) => ({ ...current, [imageKey]: false }));
};


const handleWordClick = (clickedWord: string) => {
  console.log("handleWordClick");

  // input を単語に分割
  const inputWords = input.trim().split(/\s+/);

  // どれかの単語が clickedWord に含まれているかチェック
  const isInputInWord = inputWords.some(word => clickedWord.includes(word));

  console.log("isInputInWord:", isInputInWord);

  const newInput = isInputInWord
    ? clickedWord.trim()
    : `${input.trim()} ${clickedWord}`.trim();

  setInput(newInput);
  handleSearch(newInput);
}; //handleSearch

const handleRelatedKeywordClick = (keyword: string) => {
  const nextKeyword = keyword.trim();
  if (!nextKeyword) return;

  setInput(nextKeyword);
  handleSearch(nextKeyword);
};

const isSuggestService = (value: string): value is SuggestService =>
  options.some((option) => option.value === value);

const handleSearchHistorySelect = (item: SearchHistoryItem) => {
  const nextKeyword = item.keyword.trim();
  if (!nextKeyword) return;

  const nextMode: SearchMode = item.mode === 'compare' ? 'compare' : 'single';
  const nextService = isSuggestService(item.service) ? item.service : service;
  const nextCompareService = item.compareService && isSuggestService(item.compareService)
    ? item.compareService
    : compareService;

  setInput(nextKeyword);
  setMode(nextMode);
  setService(nextService);
  setPage("analysis");
  if (nextMode === 'compare') {
    setCompareService(nextCompareService);
  }

  handleSearch(nextKeyword, nextMode, nextService, nextCompareService);
};

const handleSearchHistoryDelete = async (item: SearchHistoryItem) => {
  setSearchHistory((current) => current.filter((historyItem) => historyItem.id !== item.id));

  try {
    await axios.delete(`${API_ORIGIN}/api/search-history/${item.id}`);
  } catch (error) {
    console.warn('検索履歴の削除に失敗:', error);
    void fetchSearchHistory();
  }
};

const handleSavedKeywordSelect = (item: SavedKeywordItem) => {
  const nextKeyword = item.keyword.trim();
  if (!nextKeyword) return;

  const nextService = isSuggestService(item.service) ? item.service : service;
  setInput(nextKeyword);
  setMode('single');
  setService(nextService);
  setPage("analysis");
  handleSearch(nextKeyword, 'single', nextService);
};

const handleSavedKeywordDelete = async (item: SavedKeywordItem) => {
  setSavedKeywords((current) => current.filter((savedItem) => savedItem.id !== item.id));

  try {
    await axios.delete(`${API_ORIGIN}/api/saved-keywords/${item.id}`);
  } catch (error) {
    console.warn('保存キーワードの削除に失敗:', error);
    void fetchSavedKeywords();
  }
};

const handleRequestUpgrade = async (plan: 'basic' | 'premium', message: string) => {
  try {
    await axios.post(`${API_ORIGIN}/api/upgrade-requests`, {
      plan,
      message,
    });
  } catch (error) {
    const messageText = axios.isAxiosError(error)
      ? error.response?.data?.message || error.response?.data?.error || '申請を送信できませんでした。'
      : '申請を送信できませんでした。';
    throw new Error(messageText);
  }
};

const handleAdminApproveRequest = async (request: UpgradeRequestItem) => {
  setAdminActionLoadingId(request.id);
  try {
    await axios.post(`${API_ORIGIN}/api/admin/upgrade-requests/${request.id}/approve`);
    await fetchAdminRequests();
  } catch (error) {
    console.warn('申請承認に失敗:', error);
  } finally {
    setAdminActionLoadingId(null);
  }
};

const handleAdminRejectRequest = async (request: UpgradeRequestItem) => {
  setAdminActionLoadingId(request.id);
  try {
    await axios.post(`${API_ORIGIN}/api/admin/upgrade-requests/${request.id}/reject`);
    await fetchAdminRequests();
  } catch (error) {
    console.warn('申請却下に失敗:', error);
  } finally {
    setAdminActionLoadingId(null);
  }
};

const handleAdminUpdatePlan = async (uid: string, plan: 'free' | 'basic' | 'premium') => {
  await axios.post(`${API_ORIGIN}/api/admin/users/${uid}/plan`, { plan });
  await fetchAdminRequests();
  await fetchAdminContactMessages();
};

const handleAdminAddCoins = async (uid: string, feature: string, amount: number) => {
  await axios.post(`${API_ORIGIN}/api/admin/users/${uid}/coins`, {
    feature,
    amount,
  });
  await fetchAdminContactMessages();
};

const resetAnalysisView = () => {
  setPage("analysis");
  setInput('');
  setLoading(false);
  setShowResult(false);
  setGroupedSuggestions([]);
  setTopSuggestions([]);
  setWords([]);
  setComparisonResult(null);
  setVolumeData(null);
  setVolumeCached(false);
  setVolumeLoading(false);
  setVolumeError(null);
  setRelatedKeywordsData(null);
  setRelatedKeywordsCached(false);
  setRelatedKeywordsLoading(false);
  setRelatedKeywordsError(null);
  setAiInsightsData(null);
  setAiInsightsCached(false);
  setAiInsightsLoading(false);
  setAiInsightsError(null);
  setAiPersonasData(null);
  setAiPersonasCached(false);
  setAiPersonasLoading(false);
  setAiPersonasError(null);
  setPersonaImages({});
  setPersonaImageLoading({});
  setPersonaImageErrors({});
  setSearchError(null);
  setUsageLimitNotice(null);
};



  const totalSuggestions = groupedSuggestions.reduce(
    (sum, group) => sum + group.suggestions.length,
    0
  );
  const hasSingleResult = mode === "single" && input.trim() !== '' && showResult && !loading;
  const hasComparisonResult = mode === "compare" && input.trim() !== '' && showResult && !loading && Boolean(comparisonResult);
  const showTopPrompt = page === "analysis" && input.trim() === '' && !showResult && !loading;

  if (publicPage) {
    return (
      <LegalPage
        kind={publicPage}
        onLogoClick={() => {
          window.location.href = '/';
        }}
        onNavigate={(nextPage) => {
          window.history.pushState({}, '', '/');
          setPage(nextPage);
        }}
        isAdmin={isAdmin}
      />
    );
  }

  if (!authEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow">
          <img src="/logo_2.png" alt="NeedRadar" className="mx-auto h-14 w-auto" />
          <h1 className="mt-6 text-xl font-bold text-gray-900">Firebase が設定されていません</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            ログイン機能を使うには Firebase の環境変数を設定してください。
          </p>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="text-center">
          <img src="/logo_2.png" alt="NeedRadar" className="mx-auto h-14 w-auto" />
          <p className="mt-6 text-sm font-semibold text-gray-500">認証状態を確認中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <LandingPage
        loading={authLoading}
        error={authError}
        onLogin={() => {
          void loginWithGoogle();
        }}
        onEmailLinkLogin={sendEmailLink}
      />
    );
  }


  return (
    <div className="p-6 w-full bg-gray-50">
      <Header currentPage={page} onNavigate={setPage} onLogoClick={resetAnalysisView} isAdmin={isAdmin} />

      {page === "admin" && user && isAdmin ? (
        <>
          <AdminPage
            requests={adminRequests}
            contactMessages={adminContactMessages}
            loading={adminRequestsLoading}
            contactMessagesLoading={adminContactMessagesLoading}
            actionLoadingId={adminActionLoadingId}
            onRefresh={() => {
              void fetchAdminRequests();
              void fetchAdminContactMessages();
            }}
            onApprove={handleAdminApproveRequest}
            onReject={handleAdminRejectRequest}
            onUpdatePlan={handleAdminUpdatePlan}
            onAddCoins={handleAdminAddCoins}
          />
          <Footer />
        </>
      ) : page === "mypage" && user ? (
        <>
          <MyPage
            user={user}
            historyItems={searchHistory}
            historyLoading={searchHistoryLoading}
            savedKeywords={savedKeywords}
            savedKeywordsLoading={savedKeywordsLoading}
            usage={usageSummary}
            usageLoading={usageLoading}
            onRequestUpgrade={handleRequestUpgrade}
            onHistorySelect={handleSearchHistorySelect}
            onHistoryDelete={handleSearchHistoryDelete}
            onSavedKeywordSelect={handleSavedKeywordSelect}
            onSavedKeywordDelete={handleSavedKeywordDelete}
            onBackToAnalysis={() => setPage("analysis")}
            onLogout={logout}
          />
          <Footer />
        </>
      ) : (
        <>

      <div className="flex justify-center mb-2">
        <div className="inline-flex bg-white border border-gray-200 rounded overflow-hidden">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-semibold ${
              mode === "single"
                ? "bg-blue-500 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setMode("single")}
          >
            通常
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-semibold border-l border-gray-200 ${
              mode === "compare"
                ? "bg-blue-500 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setMode("compare")}
          >
            比較
          </button>
        </div>
      </div>

      <SearchBar
  value={input}
  onChange={setInput}
  onSearch={handleSearch}
  loading={loading}
  prompt={showTopPrompt ? topPrompt : undefined}
  rightSlot={
    mode === "compare" ? (
      <div className="flex items-center gap-2">
        <Dropdown options={options} value={service} onChange={setService} />
        <span className="text-sm font-semibold text-gray-400">vs</span>
        <Dropdown options={options} value={compareService} onChange={setCompareService} />
      </div>
    ) : (
      <Dropdown options={options} value={service} onChange={setService} />
    )
  }
/>

{(usageLimitNotice || searchError) && (
  <div className="mx-auto mt-4 max-w-3xl">
    {usageLimitNotice ? (
      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-bold">
              {usageLimitNotice.featureLabel || '利用枠'}の上限に達しました
            </p>
            <p className="mt-1 text-xs leading-5 text-amber-800">
              {usageLimitNotice.message}
            </p>
          </div>
          <div className="rounded bg-white/70 px-3 py-2 text-xs font-semibold text-amber-800">
            現在: {usageLimitNotice.plan || 'free'}
            {typeof usageLimitNotice.used === 'number' && typeof usageLimitNotice.limit === 'number'
              ? ` / ${usageLimitNotice.used}/${usageLimitNotice.limit}`
              : ''}
          </div>
        </div>
      </div>
    ) : (
      <div className="rounded border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
        {searchError}
      </div>
    )}
  </div>
)}

{/* ★ 入力が空なら案内メッセージを表示 */}
{/* {input.trim() === '' && !loading && <EmptyState />} */}

      <div className="space-y-4">
        {hasSingleResult && <SummaryCard count={totalSuggestions} />}
        

{hasSingleResult && <div className="flex flex-wrap gap-4 mt-4 justify-center">
  {topSuggestions.map((word) => (
    <WordCard key={word} word={word} onClick={handleWordClick} />
  ))}
</div>}
  
        {hasSingleResult && <CustomWordCloud
          words={words}
          onCopy={copyImageToClipboard}
          onDownload={downloadSVGAsPNG}
        />}

        {hasSingleResult && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] gap-4">
              <KeywordNetwork
                data={keywordNetworkData}
                onNodeClick={handleWordClick}
              />
              <SearchVolumeCard
                data={volumeData}
                cached={volumeCached}
                loading={volumeLoading}
                error={volumeError}
                onFetch={handleFetchVolume}
                onForceFetch={() => handleFetchVolume(true)}
              />
            </div>
            <RelatedKeywordsCard
              data={relatedKeywordsData}
              cached={relatedKeywordsCached}
              loading={relatedKeywordsLoading}
              error={relatedKeywordsError}
              onFetch={handleFetchRelatedKeywords}
              onForceFetch={() => handleFetchRelatedKeywords(true)}
              onKeywordClick={handleRelatedKeywordClick}
              onSaveKeyword={user ? (item) => saveKeyword({
                keyword: item.keyword,
                source: 'related',
                searchVolume: item.search_volume,
                cpc: item.cpc,
                competition: item.competition,
              }) : undefined}
              isKeywordSaved={user ? (keyword) => isKeywordSaved(keyword) : undefined}
            />
            <IntentAnalysisCard
              suggestions={intentAnalysisSuggestions}
              onKeywordClick={handleRelatedKeywordClick}
            />
            <AiInsightsCard
              data={aiInsightsData}
              cached={aiInsightsCached}
              loading={aiInsightsLoading}
              error={aiInsightsError}
              onFetch={handleFetchAiInsights}
              onForceFetch={() => handleFetchAiInsights(true)}
            />
            <AiPersonasCard
              data={aiPersonasData}
              cached={aiPersonasCached}
              loading={aiPersonasLoading}
              error={aiPersonasError}
              personaImages={personaImages}
              personaImageLoading={personaImageLoading}
              personaImageErrors={personaImageErrors}
              onFetch={handleFetchAiPersonas}
              onForceFetch={() => handleFetchAiPersonas(true)}
              onGeneratePersonaImage={handleGeneratePersonaImage}
            />
          </>
        )}

        {hasSingleResult && <SuggestionList
          keyword={input}
          groupedSuggestions={groupedSuggestions}
          service={service}
          onKeywordSelect={handleRelatedKeywordClick}
          onSaveKeyword={user ? (keyword) => saveKeyword({ keyword, source: 'suggestion' }) : undefined}
          isKeywordSaved={user ? (keyword) => isKeywordSaved(keyword) : undefined}
        />}

        {hasComparisonResult && comparisonResult && (
          <ComparisonView
            keyword={input}
            leftLabel={getServiceLabel(service)}
            rightLabel={getServiceLabel(compareService)}
            common={comparisonResult.common}
            leftOnly={comparisonResult.leftOnly}
            rightOnly={comparisonResult.rightOnly}
            leftCloudWords={comparisonResult.leftCloudWords}
            rightCloudWords={comparisonResult.rightCloudWords}
          />
        )}


        <Footer />
      </div>
        </>
      )}
    </div>
  );
};

export default App;
